/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description patient info schema
 */

import _ from 'lodash';
import { SelectProps } from 'antd';

import { CUSTOM_ATTRIBUTE_PREFIX } from 'utils/adfUtils';
import states from 'app-constants/states.json';
import languages from 'app-constants/languages.json';
import patientRelationships from 'app-constants/patientRelationship.json';
import { PREFERRED_CONTACT_METHODS } from 'app-constants/patients';
import { CUSTOM_ATTRIBUTE_FIELD_TYPE } from 'app-constants/admin/customAttribute';
import { YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';

const defaultSelectProps: SelectProps = {
	allowClear: true,
	showSearch: true,
	optionFilterProp: 'label',
};

const patientFormSectionKeys: Record<Patients.FormSectionKeys, Patients.FormSectionKeys> = {
	personal: 'personal',
	address: 'address',
	contact: 'contact',
	emergencyContact: 'emergencyContact',
};

// patient form keys and their fields configuration
const formKeys: Record<string, { title: string; editable: boolean; fields: Record<string, Patients.IField> }> = {
	[patientFormSectionKeys.personal]: {
		title: 'Personal',
		editable: false,
		fields: {
			firstName: {
				key: `${patientFormSectionKeys.personal}-firstName`,
				title: 'First Name',
			},
			middleName: {
				key: `${patientFormSectionKeys.personal}-middleName`,
				title: 'Middle Name',
			},
			lastName: {
				key: `${patientFormSectionKeys.personal}-lastName`,
				title: 'Last Name',
			},
			dateOfBirth: {
				key: `${patientFormSectionKeys.personal}-dateOfBirth`,
				title: 'Date of Birth',
			},
			gender: {
				key: `${patientFormSectionKeys.personal}-gender`,
				title: 'Gender',
			},
			medicalRecordNumber: {
				key: `${patientFormSectionKeys.personal}-medicalRecordNumber`,
				title: 'Medical Record Number',
			},
			socialSecurityNumber: {
				key: `${patientFormSectionKeys.personal}-socialSecurityNumber`,
				title: 'Social Security Number',
			},
			employmentStatus: {
				key: `${patientFormSectionKeys.personal}-employmentStatus`,
				title: 'Employment Status',
			},
		},
	},
	[patientFormSectionKeys.address]: {
		title: 'Address',
		editable: true,
		fields: {
			addressLine1: {
				key: `${patientFormSectionKeys.address}-addressLine1`,
				title: 'Address Line 1',
				'x-component-props': {
					placeholder: 'E.g.: 123 Oxford Valley Rd.',
				},
			},
			addressLine2: {
				key: `${patientFormSectionKeys.address}-addressLine2`,
				title: 'Address Line 2',
				'x-component-props': {
					placeholder: 'E.g.: 456 Oxford Valley Rd.',
				},
			},
			city: {
				key: `${patientFormSectionKeys.address}-city`,
				title: 'City',
				'x-component-props': {
					placeholder: 'E.g.: Yardley',
				},
			},
			state: {
				key: `${patientFormSectionKeys.address}-state`,
				title: 'State',
				'x-component': 'Select',
				'x-component-props': { options: states, ...defaultSelectProps, placeholder: 'E.g.: Pennsylvania' },
			},
			zipCode: {
				key: `${patientFormSectionKeys.address}-zipCode`,
				title: 'Zip Code',
				'x-component-props': { placeholder: 'E.g.: 19067' },
			},
		},
	},
	[patientFormSectionKeys.contact]: {
		title: 'Contact',
		editable: true,
		fields: {
			homePhone: {
				key: `${patientFormSectionKeys.contact}-homePhone`,
				title: 'Home Phone',
				'x-component-props': { placeholder: 'E.g.: 215-255-0000', type: 'number' },
			},
			cellPhone: {
				key: `${patientFormSectionKeys.contact}-cellPhone`,
				title: 'Cell Phone',
				'x-component-props': { placeholder: 'E.g.: 215-256-0000', type: 'number' },
			},
			workPhone: {
				key: `${patientFormSectionKeys.contact}-workPhone`,
				title: 'Work Phone',
				'x-component-props': { placeholder: 'E.g.: 215-257-0000', type: 'number' },
			},
			fax: {
				key: `${patientFormSectionKeys.contact}-fax`,
				title: 'Fax',
				'x-component-props': { placeholder: 'E.g.: 215-258-0000' },
			},
			emailAddress: {
				key: `${patientFormSectionKeys.contact}-emailAddress`,
				title: 'Email Address',
				'x-component-props': { placeholder: 'johndoe@gmail.com' },
			},
			preferredLanguage: {
				key: `${patientFormSectionKeys.contact}-preferredLanguage`,
				title: 'Preferred Language',
				'x-component': 'Select',
				'x-component-props': { options: languages, ...defaultSelectProps, placeholder: 'E.g.: English' },
			},
			preferredContactMethod: {
				key: `${patientFormSectionKeys.contact}-preferredContactMethod`,
				title: 'Preferred Contact Method',
				'x-component': 'Select',
				'x-component-props': { options: PREFERRED_CONTACT_METHODS, ...defaultSelectProps, placeholder: 'E.g.: Phone' },
			},
		},
	},
	[patientFormSectionKeys.emergencyContact]: {
		title: 'Emergency Contact',
		editable: true,
		fields: {
			contactName: {
				key: `${patientFormSectionKeys.emergencyContact}-contactName`,
				title: 'Contact Name',
				'x-component-props': { placeholder: 'E.g.: John Doe' },
			},
			relationToPatient: {
				key: `${patientFormSectionKeys.emergencyContact}-relationToPatient`,
				title: 'Relationship to Patient',
				'x-component': 'Select',
				'x-component-props': { options: patientRelationships, ...defaultSelectProps, placeholder: 'E.g.: Mother' },
			},
			addressLine1: {
				key: `${patientFormSectionKeys.emergencyContact}-addressLine1`,
				title: 'Address Line 1',
				'x-component-props': { placeholder: 'E.g.: 123 Oxford Valley Rd.' },
			},
			addressLine2: {
				key: `${patientFormSectionKeys.emergencyContact}-addressLine2`,
				title: 'Address Line 2',
				'x-component-props': { placeholder: 'E.g.: 456 Oxford Valley Rd.' },
			},
			city: {
				key: `${patientFormSectionKeys.emergencyContact}-city`,
				title: 'City',
				'x-component-props': { placeholder: 'E.g.: Yardley' },
			},
			state: {
				key: `${patientFormSectionKeys.emergencyContact}-state`,
				title: 'State',
				'x-component': 'Select',
				'x-component-props': { options: states, ...defaultSelectProps, placeholder: 'E.g.: Pennsylvania' },
			},
			zipCode: {
				key: `${patientFormSectionKeys.emergencyContact}-zipCode`,
				title: 'Zip Code',
				'x-component-props': { placeholder: 'E.g.: 19067' },
			},
			emailAddress: {
				key: `${patientFormSectionKeys.emergencyContact}-emailAddress`,
				title: 'Email Address',
				'x-component-props': { placeholder: 'johndoe@gmail.com' },
			},
			homePhone: {
				key: `${patientFormSectionKeys.emergencyContact}-homePhone`,
				title: 'Home Phone',
				'x-component-props': { placeholder: 'E.g.: 215-255-0000', type: 'number' },
			},
			cellPhone: {
				key: `${patientFormSectionKeys.emergencyContact}-cellPhone`,
				title: 'Cell Phone',
				'x-component-props': { placeholder: 'E.g.: 215-256-0000', type: 'number' },
			},
			workPhone: {
				key: `${patientFormSectionKeys.emergencyContact}-workPhone`,
				title: 'Work Phone',
				'x-component-props': { placeholder: 'E.g.: 215-257-0000', type: 'number' },
			},
		},
	},
};

const createOptions = (enums?: string[]) =>
	_.map(_.compact(enums), (item) => ({
		label: item,
		value: item,
	}));

// function to get patient info form schema
const getPatientInfoFormSchema = (
	entityCustomAttributeValues?: CustomAttributes.IEntityAttributesValueResponse[],
	isEditAble?: boolean,
): AnyRecord => {
	const additionalFields = {};
	_.forEach(entityCustomAttributeValues, (attr) => {
		const fieldType = attr?.attrDef?.props?.fieldType;
		const key = `${CUSTOM_ATTRIBUTE_PREFIX}${attr?.attributeKey}`;
		const title = attr?.attrDef?.jsonschema?.title;

		if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.boolean) {
			additionalFields[key] = {
				title,
				type: 'string',
				'x-component': isEditAble ? 'Switch' : 'Input',
				'x-decorator': 'FormItem',
			};
		}
		if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.date) {
			additionalFields[key] = {
				title,
				type: 'string',
				'x-component': 'DatePicker',
				'x-decorator': 'FormItem',
				'x-component-props': {
					inputDateFormat: YEAR_MONTH_DATE_FORMAT,
				},
			};
		}
		if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.list) {
			additionalFields[key] = {
				title,
				type: 'string',
				'x-component': 'Select',
				'x-decorator': 'FormItem',
				'x-component-props': {
					options: createOptions(attr?.attrDef?.jsonschema?.enum),
					placeholder: `Select ${title}`,
					...defaultSelectProps,
				},
			};
		}
		if (fieldType === CUSTOM_ATTRIBUTE_FIELD_TYPE.multiSelectList) {
			additionalFields[key] = {
				title,
				type: 'string',
				'x-component': 'Select',
				'x-decorator': 'FormItem',
				'x-component-props': {
					mode: isEditAble ? 'multiple' : 'single',
					...defaultSelectProps,
					placeholder: `Select ${title}`,
					options: createOptions(attr?.attrDef?.jsonschema?.items?.enum),
				},
			};
		}
	});
	const additionalInfoCard = {
		type: 'void',
		'x-component': 'Card',
		'x-component-props': {
			title: 'Additional Info',
			size: 'small',
			className: 'secondary-card',
		},
		properties: {
			fieldsWrapper: {
				type: 'void',
				'x-component': 'FormGrid',
				'x-component-props': {
					minColumns: 3,
					maxColumns: 3,
				},
				properties: additionalFields,
			},
		},
	};

	return {
		type: 'object',
		properties: {
			formWrapper: {
				type: 'void',
				'x-component': 'Space',
				'x-component-props': {
					direction: 'vertical',
					className: 'w-full',
					size: 16,
				},
				properties: {
					..._.mapValues(formKeys, ({ title, fields, editable }, sectionKey) => ({
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title,
							size: 'small',
							className: 'secondary-card',
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'FormGrid',
								'x-component-props': {
									minColumns: 3,
									maxColumns: 3,
								},
								properties: {
									..._.mapKeys(
										_.mapValues(fields, (field) => ({
											title: field.title,
											type: 'string',
											'x-component': field?.['x-component'] || 'Input',
											'x-decorator': 'FormItem',
											'x-component-props': {
												disabled: !editable,
												...(field?.['x-component-props'] || {}),
											},
										})),
										(__, fieldKey) => `${sectionKey}-${fieldKey}`,
									),
								},
							},
						},
					})),
					...(!_.isEmpty(entityCustomAttributeValues) ? { additionalInfo: additionalInfoCard } : {}),
				},
			},
		},
	};
};

export { patientFormSectionKeys, formKeys, PREFERRED_CONTACT_METHODS };
export default getPatientInfoFormSchema;
